"use client";

import Link from "next/link";
import { redirect, useSearchParams } from "next/navigation";
import { signIn } from "next-auth/react";
import { dashboardPaths, forgotPasswordPaths } from "routes";
import useSWRMutation from "swr/mutation";
import * as z from "zod";

import Button from "@/components/atoms/Button";
import TextInput from "@/components/forms/controls/TextInput";
import Form from "@/components/forms/Form";
import { useZodForm } from "@/hooks/useZodForm";
import { useTranslation } from "@/i18n/client";
import { AllowedLanguages } from "@/types/generic";

interface LoginFormProps {
  className?: string;
  locale: AllowedLanguages;
}

export const LoginForm = ({ locale, className }: LoginFormProps) => {
  const { t } = useTranslation(locale);

  const schema = z.object({
    email: z.email(t("common.error.invalid_email")).trim().toLowerCase(),
    password: z.string().trim().min(1, t("common.error.required")),
  });

  const form = useZodForm({ schema });
  const searchParams = useSearchParams();

  async function login(
    url: string,
    {
      arg,
    }: {
      arg: z.infer<typeof schema>;
    },
  ) {
    return await signIn("credentials", {
      email: arg.email,
      password: arg.password,
      callbackUrl: `/${locale}/myvivaldis/${dashboardPaths[locale]}`,
      redirect: false,
    });
  }

  const { data, trigger, isMutating } = useSWRMutation(
    "/api/auth/callback/credentials",
    login,
  );

  const handleWorkIDLogin = () => {
    void signIn("workid");
  };

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    await trigger(data);
  };

  if (data?.ok) {
    if (data.url) {
      window.location.href = data.url;
    } else {
      redirect(`/${locale}/myvivaldis/profile`);
    }
  }

  return (
    <div className={className}>
      <h2 className="h3 !mb-6">
        {t("pages.myvivaldis_login.login_form.form_title")}
      </h2>
      {searchParams.get("password_reset") && (
        <div className="mb-4 rounded bg-positive-100 px-4 py-3">
          <h6 className="!mb-0">
            {t(
              "pages.myvivaldis_login.login_form.password_reset_success.title",
            )}
          </h6>
          <div>
            {t(
              "pages.myvivaldis_login.login_form.password_reset_success.message",
            )}
          </div>
        </div>
      )}
      {searchParams.get("password_set") && (
        <div className="mb-4 rounded bg-positive-100 px-4 py-3">
          <h6 className="!mb-0">
            {t("pages.myvivaldis_login.login_form.password_set_success.title")}
          </h6>
          <div>
            {t(
              "pages.myvivaldis_login.login_form.password_set_success.message",
            )}
          </div>
        </div>
      )}
      {searchParams.get("user_activated") && (
        <div className="mb-4 rounded bg-positive-100 px-4 py-3">
          <h6 className="!mb-0">
            {t(
              "pages.myvivaldis_login.login_form.user_activated_success.title",
            )}
          </h6>
          <div>
            {t(
              "pages.myvivaldis_login.login_form.user_activated_success.message",
            )}
          </div>
        </div>
      )}
      {searchParams.get("error") && (
        <div className="mb-1 py-1 text-red">{searchParams.get("error")}</div>
      )}
      {searchParams.get("error_description") && (
        <div className="mb-1 py-1 text-red">
          {searchParams.get("error_description")}
        </div>
      )}

      <Form
        form={form}
        className="flex flex-col gap-6"
        onSubmit={handleSubmit}
        noValidate={true}
      >
        <TextInput
          label={t("common.form.email.label")}
          placeholder={t("common.form.email.placeholder")}
          name="email"
          type="email"
          autoComplete="email"
          required
        />

        <TextInput
          label={t("common.form.password.label")}
          placeholder={t("common.form.password.placeholder")}
          name="password"
          type="password"
          autoComplete="current-password"
          required
        />

        {data?.ok === false && (
          <div className="text-red">
            {data.error === "not_activated"
              ? t("pages.myvivaldis_login.login_form.error.not_activated")
              : t(
                  "pages.myvivaldis_login.login_form.error.invalid_credentials",
                )}
          </div>
        )}

        <div className="mt-4 flex flex-wrap-reverse items-center justify-end gap-x-10 gap-y-6 lg:mt-6">
          <Link
            href={`/${locale}/myvivaldis/${forgotPasswordPaths[locale]}`}
            className="link"
          >
            {t("pages.myvivaldis_login.login_form.forgot_password_link")}
          </Link>

          <Button
            type="submit"
            disabled={isMutating}
            hasLoader={true}
            isLoading={isMutating}
            className="btn-lg"
          >
            {t("pages.myvivaldis_login.login_form.submit_button")}
          </Button>
        </div>
        <div className="mt-4 flex items-center">
          <div className="border-1 flex-grow border-t border-dashed"></div>
          <div className="px-3 uppercase text-black-300 lg:px-4">
            {t("pages.myvivaldis_login.login_form.or")}
          </div>
          <div className="border-1 flex-grow border-t border-dashed"></div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-4">
          {/* WorkID Login Button */}
          <Button
            type="button"
            icon={"workid"}
            iconClassName={"text-[#316FF6] -translate-y-[.1rem]"}
            arrow={false}
            onClick={handleWorkIDLogin}
            className="btn btn-workid btn-social-login--workid 0 w-full gap-2 border-border bg-white font-semibold text-black hover:bg-gray-100"
          >
            {t("pages.myvivaldis_login.login_form.work_id_button")}
          </Button>
        </div>
      </Form>
    </div>
  );
};
